import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

/* Removed the TypeScript interface and type annotations because the project is compiled as plain JavaScript.
   This eliminates the syntax error that occurs when the parser encounters TypeScript‑only syntax
   (e.g., `id: number;`). The component now works with plain objects while keeping the same shape. */

const successCases = [
  {
    id: 1,
    name: 'Marcos Oliveira',
    role: 'Proprietário',
    company: 'Barbearia Premium',
    image:
      'https://public.readdy.ai/ai/img_res/5642c9fe95bbf870be73e6dd81b19663.jpg',
    avatar:
      'https://readdy.ai/api/search-image?query=brazilian-man-barbershop-owner-age-32-short-black-hair-beard-wearing-black-polo-shirt-confident-smile-professional-portrait-neutral-background-studio-lighting-high-quality-photography&width=200&height=200&seq=owner-marcos-oliveira&orientation=squarish',
    metric: '+75%',
    metricLabel: 'Faturamento',
    quote:
      'A IA mudou completamente meu negócio. Antes eu perdia clientes por não responder rápido, agora minha agenda está sempre lotada.',
    industry: 'Barbearia',
    industryIcon: 'ri-scissors-line',
  },
  {
    id: 2,
    name: 'Dra. Camila Santos',
    role: 'Diretora Clínica',
    company: 'Clínica Estética Renova',
    image:
      'https://readdy.ai/api/search-image?query=modern-aesthetic-clinic-interior-clean-white-minimalist-design-treatment-room-with-professional-medical-equipment-soft-ambient-lighting-luxury-spa-atmosphere-happy-staff-and-patients-professional-photography&width=800&height=600&seq=success2&orientation=landscape',
    avatar:
      'https://readdy.ai/api/search-image?query=brazilian-woman-doctor-age-42-long-brown-hair-wearing-white-medical-coat-elegant-professional-warm-smile-confident-expression-neutral-background-medical-professional-portrait-studio-lighting&width=200&height=200&seq=owner-camila-santos&orientation=squarish',
    metric: '+81%',
    metricLabel: 'Consultas',
    quote:
      'O follow-up automático recuperou pacientes que tinham sumido. Resultado incrível em apenas 2 meses de uso.',
    industry: 'Clínica Estética',
    industryIcon: 'ri-heart-pulse-line',
  },
  {
    id: 3,
    name: 'Ricardo Mendes',
    role: 'Corretor de Imóveis',
    company: 'RM Imobiliária',
    image:
      'https://readdy.ai/api/search-image?query=luxury-real-estate-office-interior-modern-design-with-large-windows-city-skyline-view-elegant-furniture-professional-workspace-warm-lighting-high-end-corporate-environment-architectural-photography&width=800&height=600&seq=success3&orientation=landscape',
    avatar:
      'https://readdy.ai/api/search-image?query=brazilian-real-estate-agent-man-age-45-gray-hair-wearing-navy-blue-suit-white-shirt-confident-businessman-friendly-smile-professional-corporate-headshot-neutral-background-executive-portrait&width=200&height=200&seq=owner-ricardo-mendes&orientation=squarish',
    metric: '+138%',
    metricLabel: 'Vendas',
    quote:
      'A IA qualifica os leads automaticamente. Agora só falo com quem realmente quer comprar, economizo horas por dia.',
    industry: 'Imobiliária',
    industryIcon: 'ri-building-line',
  },
];

export default function VideoTestimonialsSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-black via-gray-950 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-0 w-[250px] sm:w-[500px] h-[250px] sm:h-[500px] bg-primary rounded-full blur-[150px]"></div>
        <div className="absolute bottom-1/4 right-0 w-[250px] sm:w-[500px] h-[250px] sm:h-[500px] bg-cyan-500 rounded-full blur-[150px]"></div>
      </div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <span
            className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 text-primary text-xs sm:text-sm font-semibold rounded-full mb-4 sm:mb-6 transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <i className="ri-trophy-line"></i>
            Casos de Sucesso
          </span>
          <h2
            className={`text-2xl sm:text-3xl md:text-5xl font-bold mb-4 sm:mb-6 transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            Resultados <span className="text-primary">Reais</span> de Clientes Reais
          </h2>
          <p
            className={`text-gray-400 text-sm sm:text-lg max-w-2xl mx-auto px-2 transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            Empresários que transformaram seus negócios com a NeuroAgents
          </p>
        </div>

        {/* Featured Case */}
        <div
          className={`mb-8 sm:mb-12 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
          style={{ transitionDelay: '300ms' }}
        >
          <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden bg-gray-900 border border-gray-800 group hover:border-primary/50 transition-all duration-500">
            <div className="grid md:grid-cols-2">
              {/* Image Side */}
              <div className="relative h-64 sm:h-80 md:h-full min-h-[300px]">
                <img
                  src={successCases[0].image}
                  alt={`Caso de sucesso - ${successCases[0].company}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-gray-900 hidden md:block"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent md:hidden"></div>

                {/* Industry Badge */}
                <div className="absolute top-4 left-4 px-3 py-1.5 bg-primary/90 backdrop-blur-sm rounded-lg text-white text-sm font-semibold flex items-center gap-2">
                  <i className={successCases[0].industryIcon}></i>
                  {successCases[0].industry}
                </div>
              </div>

              {/* Content Side */}
              <div className="p-6 sm:p-8 md:p-10 flex flex-col justify-center">
                {/* Metric */}
                <div className="mb-6">
                  <div className="inline-flex items-baseline gap-2 bg-primary/10 border border-primary/30 rounded-2xl px-6 py-4">
                    <span className="text-4xl sm:text-5xl md:text-6xl font-bold text-primary">
                      {successCases[0].metric}
                    </span>
                    <span className="text-lg sm:text-xl text-primary/80">{successCases[0].metricLabel}</span>
                  </div>
                </div>

                {/* Quote */}
                <blockquote className="text-lg sm:text-xl md:text-2xl text-gray-200 mb-6 leading-relaxed">
                  &quot;{successCases[0].quote}&quot;
                </blockquote>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <img
                    src={successCases[0].avatar}
                    alt={successCases[0].name}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-3 border-primary object-cover"
                  />
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white">{successCases[0].name}</h3>
                    <p className="text-gray-400 text-sm sm:text-base">
                      {successCases[0].role} • {successCases[0].company}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cases Grid */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
          {successCases.slice(1).map((caseItem, index) => (
            <div
              key={caseItem.id}
              className={`relative rounded-xl sm:rounded-2xl overflow-hidden bg-gray-900 border border-gray-800 hover:border-primary/50 transition-all duration-500 group ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${400 + index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative h-48 sm:h-56">
                <img
                  src={caseItem.image}
                  alt={`Caso de sucesso - ${caseItem.company}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>

                {/* Industry Badge */}
                <div className="absolute top-3 left-3 px-2.5 py-1 bg-primary/90 backdrop-blur-sm rounded-lg text-white text-xs font-semibold flex items-center gap-1.5">
                  <i className={caseItem.industryIcon}></i>
                  {caseItem.industry}
                </div>

                {/* Metric Badge */}
                <div className="absolute top-3 right-3 px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-lg">
                  <span className="text-primary font-bold text-lg">{caseItem.metric}</span>
                  <span className="text-gray-300 text-xs ml-1">{caseItem.metricLabel}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6">
                {/* Quote */}
                <p className="text-gray-300 text-sm sm:text-base mb-4 line-clamp-3">
                  &quot;{caseItem.quote}&quot;
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <img
                    src={caseItem.avatar}
                    alt={caseItem.name}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-primary/50 object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-white text-sm sm:text-base">{caseItem.name}</h4>
                    <p className="text-gray-400 text-xs sm:text-sm">{caseItem.company}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className={`mt-10 sm:mt-16 text-center transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '600ms' }}
        >
          <p className="text-gray-400 text-xs sm:text-base mb-4 sm:mb-6 px-4">
            Quer resultados como esses para o seu negócio?
          </p>
          <a
            href="https://wa.me/5541992584018?text=Olá! Quero resultados como esses clientes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-primary/30 cursor-pointer whitespace-nowrap text-sm sm:text-base"
          >
            <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
            Quero Meus Resultados Também
          </a>
        </div>

        {/* Trust Badges */}
        <div
          className={`mt-8 sm:mt-12 flex flex-wrap justify-center items-center gap-4 sm:gap-6 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '700ms' }}
        >
          <div className="flex items-center gap-1.5 sm:gap-2 text-gray-500 text-[10px] sm:text-sm">
            <i className="ri-verified-badge-fill text-green-500"></i>
            <span>Resultados Verificados</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 text-gray-500 text-[10px] sm:text-sm">
            <i className="ri-user-star-line text-primary"></i>
            <span>Clientes Reais</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 text-gray-500 text-[10px] sm:text-sm">
            <i className="ri-shield-check-fill text-primary"></i>
            <span>Dados Comprovados</span>
          </div>
        </div>
      </div>
    </section>
  );
}
